import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useMFE, { type MFEsState } from '@/hooks/useMFE';
import { setAnalyticsAuthSettled, setAnalyticsUserId, setUserAuthState } from '@/utils/analytics';

export type MFEs = 'settings' | 'auth' | 'garage';

interface AppContextType {
	isProcessing: boolean;
	isLoggedIn: boolean;
	authSettled: boolean;
	authBootAnswered: boolean;
	mountOptions: Record<string, unknown>;
	auth: MFEsState;
	settings: MFEsState;
	garage: MFEsState;
	renderMFE: MFEs | null;
	redirectPath: string | null;
	hasSessionCookies: boolean;
	bootAttempt: number;
	retryBoot: () => void;
	setAuthBootAnswered: (answered: boolean) => void;
	setIsLoggedIn: (loggedIn: boolean) => void;
	setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
	setMountOptions: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
	setAuthState: (state: Partial<MFEsState>) => void;
	setSettingsState: (state: Partial<MFEsState>) => void;
	setGarageState: (state: Partial<MFEsState>) => void;
	setRenderMFE: React.Dispatch<React.SetStateAction<MFEs | null>>;
	setRedirectPath: (url: string | null) => void;
}

export const AppContext = createContext<AppContextType>({} as AppContextType);

interface AppContextProviderProps {
	children: React.ReactNode;
	/** Whether the document request carried an `sk_` session cookie. */
	hasSessionCookies: boolean;
}

/**
 * AppContextProvider component that provides application-wide state and functionality
 * through React Context. This provider wraps the application and makes state variables
 * and their setters available to all child components.
 *
 * @param props - Component props
 * @param props.children - Child components to be wrapped by the provider
 *
 * @returns JSX.Element containing the context provider with all child components
 *
 * State Variables:
 * - isProcessing: Boolean indicating if a process is currently running
 * - setIsProcessing: Function to update the processing state
 * - mountOptions: Object containing options for mounting MFEs
 * - setMountOptions: Function to update mount options
 * - renderMFE: Currently active micro-frontend to render ('settings' | 'auth' | 'garage' | null)
 * - setRenderMFE: Function to set which MFE to render
 * - auth, settings, garage: State objects for respective MFEs
 * - setAuthState, setSettingsState, setGarageState: Functions to update respective MFE states
 */
export const AppContextProvider = ({
	children,
	hasSessionCookies
}: AppContextProviderProps): React.JSX.Element => {
	const [isProcessing, setIsProcessing] = useState(true);
	const [isLoggedIn, setIsLoggedInState] = useState(false);
	const [authSettled, setAuthSettled] = useState(false);
	const [mountOptions, setMountOptions] = useState({});
	const [renderMFE, setRenderMFE] = useState<MFEs | null>(null);
	const { mfes, setAuthState, setSettingsState, setGarageState } = useMFE();
	const [redirectPath, setRedirectPath] = useState<string | null>(null);
	const [bootAttempt, setBootAttempt] = useState(0);
	const retryBoot = useCallback(() => setBootAttempt((attempt) => attempt + 1), []);
	const [authBootAnswered, setAuthBootAnswered] = useState(false);
	// Synchronous, so an MFE view sent right after a login or logout isn't stamped with the old state;
	// an effect would also miss a batched true→false.
	const setIsLoggedIn = useCallback((loggedIn: boolean) => {
		if (!loggedIn) setAnalyticsUserId(null);
		setUserAuthState(loggedIn ? 'authed' : 'anon');
		setIsLoggedInState(loggedIn);
	}, []);

	// Latch once the shell finishes its initial processing (the auth MFE's initialization_complete
	// flips isProcessing false). Gating the first page_view on this — see RouteAnalytics — keeps the
	// session-entry event from being stamped 'anon' before auth resolves for returning logged-in
	// users. This is the same signal that gates the app's own loading overlay, so it adds no new
	// failure mode: if it never settles, the shell never renders anyway.
	useEffect(() => {
		if (!isProcessing) setAuthSettled(true);
	}, [isProcessing]);

	useEffect(() => {
		if (authSettled) setAnalyticsAuthSettled(true);
	}, [authSettled]);

	const value: AppContextType = useMemo(() => {
		return {
			isProcessing,
			isLoggedIn,
			authSettled,
			authBootAnswered,
			mountOptions,
			...mfes,
			renderMFE,
			redirectPath,
			hasSessionCookies,
			bootAttempt,
			retryBoot,
			setAuthBootAnswered,
			setRenderMFE,
			setAuthState,
			setSettingsState,
			setGarageState,
			setIsLoggedIn,
			setIsProcessing,
			setMountOptions,
			setRedirectPath
		};
	}, [
		isProcessing,
		isLoggedIn,
		authSettled,
		authBootAnswered,
		mountOptions,
		mfes,
		renderMFE,
		redirectPath,
		hasSessionCookies,
		bootAttempt,
		retryBoot,
		setIsLoggedIn,
		setAuthState,
		setSettingsState,
		setGarageState
	]);

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
	const context = useContext(AppContext);
	if (!Object.keys(context).length) {
		throw new Error('useAppContext must be used within an AppContextProvider');
	}
	return context;
};
